import { ReactNode, useState } from "react";
import { createPortal } from "react-dom";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import { ConfirmContent, ItemWithMetaData } from "../api/Items";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import { SocialIcon } from "../utils/social-icon";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { MatchingEntitiesConfirm } from "./ProfileRowConfirm";
import ReactTooltip from "react-tooltip";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import {
  ConfirmDialogActions,
  useConfirmationDialog,
} from "../shared-components/ConfirmationDialog";
import {
  DiscoveryMatch,
  TrustBucket,
  TRUST_BUCKET_LABELS,
  discoveryMatchChips,
  getProfileTrust,
  sortProfilesByTrustScore,
} from "./util";
import EntityUsedToConfirmModal from "./EntityUsedToConfirmModal";
import {
  mapDiscoveryMatchesToConfirmEntities,
  ProfileAddConfirmation,
} from "./profileConfirmEntities";
import { resolveProfileLocationDisplay } from "./profileLocationDisplay";

// Shape of the rich profile payload persisted by Vetric discovery. All fields
// are optional; the card only renders the groups that actually have data.
interface ProfileMedia {
  internal_url?: string;
  external_url?: string;
  type?: string;
}

interface ProfileJob {
  title?: string;
  employer?: string;
  start_date?: string;
  end_date?: string;
}

interface ProfileEducation {
  educator?: string;
  majors?: string[];
  start_date?: string;
  end_date?: string;
}

interface ProfileName {
  first_name?: string;
  middle_name?: string;
  last_name?: string;
}

interface ProfileAddress {
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

interface ProfileContent {
  url?: string;
  site?: string;
  username?: string;
  added_by?: string;
  display_name?: string;
  verified?: boolean;
  is_private?: boolean;
  media?: ProfileMedia[];
  created_at?: string;
  location?: string;
  bio?: string;
  bio_links?: string[];
  follower_count?: number;
  following_count?: number;
  post_count?: number;
  jobs?: ProfileJob[];
  education?: ProfileEducation[];
  languages?: { name?: string }[];
  names?: ProfileName[];
  emails?: string[];
  phones?: string[];
  gender?: string;
  date_of_birth?: string;
  addresses?: ProfileAddress[];
  linked_social_profiles?: string[];
  discovery_sources?: string[];
  discovery_matches?: DiscoveryMatch[];
  trust_score?: number;
  trust_bucket?: string;
}

// Figma renders the trust bucket as a large outlined chip with dark, bold text and a
// bucket-colored border on a white background (not a small filled badge).
const BUCKET_PILL_STYLES: Record<TrustBucket, string> = {
  strong: "bg-white text-gray-900 border border-green-400",
  potential: "bg-white text-gray-900 border border-amber-400",
  // Same styling as Potential Match — web-content-scraper fallback chip.
  review: "bg-white text-gray-900 border border-amber-400",
  needs_verification: "bg-white text-gray-700 border border-gray-300",
};

// Profiler (ENABLE_PROFILER) variant: smaller chips with a pale filled background
// (green / orange / grey) instead of just a colored border.
const BUCKET_PILL_STYLES_COMPACT: Record<TrustBucket, string> = {
  strong: "bg-green-100 text-gray-700 border border-green-400",
  potential: "bg-orange-200 text-orange-800 border border-orange-400",
  // Same styling as Potential Match — web-content-scraper fallback chip.
  review: "bg-orange-200 text-orange-800 border border-orange-400",
  needs_verification: "bg-gray-200 text-gray-700 border border-gray-400",
};

const formatDate = (value?: string): string | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${date.getFullYear()}`;
};

const formatMonthYear = (value?: string): string | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const formatJobRange = (job: ProfileJob): string | undefined => {
  const start = formatMonthYear(job.start_date);
  const end = job.end_date ? formatMonthYear(job.end_date) : "Present";
  if (!start && !job.end_date) return undefined;
  if (!start) return end;
  return `${start} \u2013 ${end}`;
};

const fullName = (name?: ProfileName): string | undefined => {
  if (!name) return undefined;
  const value = [name.first_name, name.middle_name, name.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return value || undefined;
};

const isLinkedIn = (content: ProfileContent): boolean => {
  const target = `${content.site ?? ""} ${content.url ?? ""}`.toLowerCase();
  return target.includes("linkedin");
};

const isYouTube = (content: ProfileContent): boolean => {
  const target = `${content.site ?? ""} ${content.url ?? ""}`.toLowerCase();
  return target.includes("youtube");
};

// YouTube counts posts as videos; everything else reads as "Post Count".
const postCountLabel = (content: ProfileContent): string =>
  isYouTube(content) ? "Video Count:" : "Post Count:";

// Canonical key for a link so cosmetic variants (scheme, www, trailing slash,
// casing) collapse to one value when deduping Bio Links against Social Links.
const canonicalLinkKey = (link: string): string =>
  link
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/+$/, "");

// linked_social_profiles reads as "Affiliations" on X/Twitter and as
// "Social Links" on other platforms, matching the Figma variants.
const socialLinksLabel = (content: ProfileContent): string => {
  const target = `${content.site ?? ""} ${content.url ?? ""}`.toLowerCase();
  if (target.includes("x.com") || target.includes("twitter")) {
    return "Affiliations";
  }
  return "Social Links";
};

// Exported for unit tests (click-to-enlarge + portal escape from overflow
// scroll containers in Review/Added columns).
export const ProfilerAvatar = ({
  sources,
  name,
}: {
  sources?: (string | undefined)[];
  name: string;
}) => {
  // Try the re-hosted S3 (internal) URL first, then the original CDN (external) URL as a
  // backup. Hotlink-protected IG/FB CDN URLs expire/403, so internal_url is what actually
  // renders. Failures are tracked by URL (not index) so list re-sorting can't leave a stale
  // "errored" flag pointing at the wrong image.
  const candidates = (sources ?? []).filter(
    (url, index, all): url is string => !!url && all.indexOf(url) === index
  );
  const [failed, setFailed] = useState<Record<string, true>>({});
  const [enlarged, setEnlarged] = useState(false);
  const initial = name?.trim()?.charAt(0)?.toUpperCase() || "?";
  const src = candidates.find((url) => !failed[url]);
  if (!src) {
    return (
      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 text-lg font-semibold text-gray-600">
        {initial}
      </div>
    );
  }
  return (
    <>
      <img
        src={src}
        alt={name}
        onError={() => setFailed((prev) => ({ ...prev, [src]: true }))}
        onClick={() => setEnlarged(true)}
        className="h-12 w-12 flex-shrink-0 cursor-pointer rounded-full object-cover"
      />
      {enlarged &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
            onClick={() => setEnlarged(false)}
            role="dialog"
            aria-label={`${name} profile picture`}
          >
            <img
              src={src}
              alt={name}
              className="max-h-[80vh] max-w-[80vw] rounded-lg object-contain shadow-2xl"
            />
          </div>,
          document.body
        )}
    </>
  );
};

const LabeledLine = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <p className="text-sm leading-5 text-gray-700">
    <span className="font-semibold">{label}</span> {children}
  </p>
);

const EXTERNAL_LINK_CLASS = "text-sky-600 hover:underline break-all";

const ExternalLink = ({
  href,
  children,
}: {
  href: string;
  children: string;
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={EXTERNAL_LINK_CLASS}
  >
    {children}
  </a>
);

const ExternalLinkList = ({ links }: { links: string[] }) => (
  <>
    {links.map((link, index) => (
      <span key={`${link}-${index}`}>
        {index > 0 && ", "}
        <ExternalLink href={link}>{link}</ExternalLink>
      </span>
    ))}
  </>
);

const InfoChip = ({ label, value }: { label: string; value: string }) => (
  <span className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-600">
    {label}: <span className="font-semibold text-gray-800">{value}</span>
  </span>
);

// Shared delete safeguards for the profiler cards/rows, mirroring the legacy
// ProfileRowConfirm: only allow removal with permission, and require a
// confirmation that warns the profile's posts are removed too.
export const useProfileRemoveGuard = (
  profile: ItemWithMetaData,
  handleDelete?: (itemUUID: string) => void,
  options?: { skipConfirmation?: boolean }
) => {
  const { canDeleteClientAddedProfile, canInternalQaEdit, canClientQaEdit } =
    usePermissionValidator();
  const { modal, hide, show } = useConfirmationDialog();
  const itemIsLowPermission =
    profile.item.content?.added_by === "INVESTIGATOR" &&
    !profile.item.is_reviewed;
  const canDeleteProtectedProfile =
    canDeleteClientAddedProfile || canInternalQaEdit || canClientQaEdit;
  const canDelete = itemIsLowPermission || canDeleteProtectedProfile;

  const confirmRemove = () => {
    if (options?.skipConfirmation) {
      handleDelete?.(profile.item.external_id);
      return;
    }

    show(
      "Remove Profile?",
      "Removing this profile will also remove all posts associated with this profile. Do you still wish to continue?",
      (action: ConfirmDialogActions) => {
        if (action === "continue") {
          handleDelete?.(profile.item.external_id);
          hide();
        }
      }
    );
  };

  return {
    canDelete,
    confirmRemove,
    modal: options?.skipConfirmation ? null : modal,
  };
};

export const ProfileCard = ({
  profile,
  isReadOnly,
  handleAddRating,
  handleDelete,
  showTrustBadge,
  compactBadge,
  showAddToReport = true,
  showMatchingEntities,
  detailsCompact,
  pendingItemUUIDs,
  pendingRemoveToastUUIDs,
  skipRemoveConfirmation,
}: {
  profile: ItemWithMetaData;
  isReadOnly?: boolean;
  handleAddRating?: (
    itemUUID: string,
    confirmation?: ProfileAddConfirmation,
    existingContent?: ConfirmContent
  ) => void;
  handleDelete?: (itemUUID: string) => void;
  showTrustBadge: boolean;
  // Profiler variant: render the trust pill smaller with a pale filled background.
  compactBadge?: boolean;
  // Whether the "Add to Report" action is shown (e.g. hidden for already-added profiles).
  showAddToReport?: boolean;
  // Controls the bottom section: when undefined, falls back to the legacy behavior
  // (MatchingEntitiesConfirm shown only when the trust badge is hidden).
  showMatchingEntities?: boolean;
  // Hides joined/location, bio, experience, chips, etc. and shows a "More info" placeholder.
  detailsCompact?: boolean;
  pendingItemUUIDs?: ReadonlySet<string>;
  pendingRemoveToastUUIDs?: ReadonlySet<string>;
  skipRemoveConfirmation?: boolean;
}) => {
  const content: ProfileContent = profile.item.content || {};
  const { bucket } = getProfileTrust(profile);

  // Mirror the legacy ProfileRowConfirm delete safeguards: only expose Remove to
  // users with permission, and require a confirmation that warns posts will also
  // be deleted before the destructive call fires.
  const {
    canDelete: canDeleteThisProfile,
    confirmRemove: handleRemoveProfile,
    modal,
  } = useProfileRemoveGuard(profile, handleDelete, {
    skipConfirmation: skipRemoveConfirmation,
  });

  const url = content.url;
  const title = content.display_name || content.username || url || "Profile";
  const joined = formatDate(content.created_at);
  const metaSegments: { label: string; value: string }[] = [];
  if (typeof content.post_count === "number") {
    metaSegments.push({
      label: postCountLabel(content),
      value: content.post_count.toLocaleString("en-US"),
    });
  }
  if (joined) metaSegments.push({ label: "Joined:", value: joined });
  const { locationLabel, addressChip } = resolveProfileLocationDisplay(content);
  if (locationLabel)
    metaSegments.push({ label: "Location:", value: locationLabel });

  const bioLabel = isLinkedIn(content) ? "Headline:" : "Bio:";
  const jobs = (content.jobs ?? []).filter((job) => job.title || job.employer);

  const socialLinks = (content.linked_social_profiles ?? []).filter(Boolean);
  // The backend merges linked_social_profiles into bio_links (they "also count as bio
  // links" for cross-platform following), so the same URL can appear in both fields. Show
  // each link once: keep it under Social Links and drop the duplicate from Bio Links.
  const socialLinkKeys = new Set(socialLinks.map(canonicalLinkKey));
  const bioLinks = (content.bio_links ?? [])
    .filter(Boolean)
    .filter((link) => !socialLinkKeys.has(canonicalLinkKey(link)));

  const primaryName = fullName(content.names?.[0]);
  const institution =
    content.education?.[0]?.educator || content.jobs?.[0]?.employer;
  const address = addressChip;
  const email = content.emails?.[0];
  const phone = content.phones?.[0];
  const dob = formatDate(content.date_of_birth);

  // In the profiler/trust view every chip must represent a real match against the
  // report, so the row shows ONLY discoveryMatchChips. The legacy (non-trust) view
  // keeps the raw profile-attribute chips it has always rendered.
  const chips: { label: string; value: string }[] = [];
  if (showTrustBadge) {
    chips.push(...discoveryMatchChips(content.discovery_matches));
  } else {
    if (primaryName) chips.push({ label: "Name", value: primaryName });
    if (institution) chips.push({ label: "Institution", value: institution });
    if (address) chips.push({ label: "Address", value: address });
    if (email) chips.push({ label: "Email", value: email });
    if (phone) chips.push({ label: "Phone", value: phone });
    if (content.gender) chips.push({ label: "Gender", value: content.gender });
    if (dob) chips.push({ label: "DOB", value: dob });
  }

  // Legacy behavior: the matching-entities block only shows when the trust badge
  // is hidden. The profiler view overrides this explicitly via the prop.
  const renderMatchingEntities = showMatchingEntities ?? !showTrustBadge;
  const isPending = pendingItemUUIDs?.has(profile.item.external_id) ?? false;
  const isRemoving =
    pendingRemoveToastUUIDs?.has(profile.item.external_id) ?? false;
  const [entityModalOpen, setEntityModalOpen] = useState(false);

  const handleEntityConfirmSubmit = (confirmation: ProfileAddConfirmation) => {
    handleAddRating?.(
      profile.item.external_id,
      confirmation,
      profile.item.content
    );
    setEntityModalOpen(false);
  };

  if (isRemoving) {
    return null;
  }

  return (
    <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      {showTrustBadge && (
        <div className="mb-3">
          <span
            className={
              compactBadge
                ? `inline-block rounded-full px-2 py-0.5 text-xs font-medium ${BUCKET_PILL_STYLES_COMPACT[bucket]}`
                : `inline-block rounded-full px-3 py-1 text-sm font-semibold ${BUCKET_PILL_STYLES[bucket]}`
            }
          >
            {TRUST_BUCKET_LABELS[bucket]}
          </span>
        </div>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <ProfilerAvatar
            sources={[
              content.media?.[0]?.internal_url,
              content.media?.[0]?.external_url,
            ]}
            name={title}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-lg font-semibold text-gray-900">
                {title}
              </span>
              {content.verified && (
                <CheckBadgeIcon
                  className="h-5 w-5 flex-shrink-0 text-blue-500"
                  aria-label="Verified"
                />
              )}
            </div>
            <span className="mt-0.5 inline-block rounded-full border border-gray-300 px-2 py-0.5 text-xs font-medium text-gray-600">
              {content.is_private ? "Private" : "Public"}
            </span>
          </div>
        </div>
        {!isReadOnly && (
          <div className="flex flex-shrink-0 items-center gap-2">
            {isPending ? (
              <LoadingSpinner className="h-5 w-5" />
            ) : (
              <>
                {canDeleteThisProfile && (
                  <Button
                    text=""
                    type={ButtonTypes.remove}
                    colorType={ButtonColorTypes.cancel}
                    onClick={handleRemoveProfile}
                  />
                )}
                {showAddToReport && (
                  <Button
                    text=""
                    type={ButtonTypes.add}
                    colorType={ButtonColorTypes.warning}
                    onClick={() => setEntityModalOpen(true)}
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="my-3 border-t border-gray-200" />

      {url && (
        <div className="flex items-center gap-0.5">
          <SocialIcon url={url} style={{ height: 22, width: 22 }} />
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-lg font-bold text-sky-600 hover:underline"
            data-tip={url}
          >
            {content.username ? `@${content.username}` : url}
          </a>
          <ReactTooltip
            className="hover:visible:opacity-1 w-96"
            delayHide={1000}
            effect="solid"
          />
        </div>
      )}

      {detailsCompact ? (
        <p className="mt-2 text-sm text-gray-500">
          Profile details hidden in compact view
        </p>
      ) : (
        <>
          {metaSegments.length > 0 && (
            <p className="mt-2 text-sm text-gray-700">
              {metaSegments.map((segment, index) => (
                <span key={segment.label}>
                  {index > 0 && <span className="mx-2 text-gray-300">|</span>}
                  <span className="font-semibold">{segment.label}</span>{" "}
                  {segment.value}
                </span>
              ))}
            </p>
          )}

          <div className="mt-2 space-y-1.5">
            {content.bio && (
              <LabeledLine label={bioLabel}>{content.bio}</LabeledLine>
            )}

            {bioLinks.length > 0 && (
              <LabeledLine label="Bio Links:">
                <ExternalLinkList links={bioLinks} />
              </LabeledLine>
            )}

            {socialLinks.length > 0 && (
              <LabeledLine label={`${socialLinksLabel(content)}:`}>
                <ExternalLinkList links={socialLinks} />
              </LabeledLine>
            )}

            {jobs.length > 0 && (
              <div className="text-sm text-gray-700">
                <p className="font-semibold">Experience History:</p>
                {jobs.map((job, index) => {
                  const range = formatJobRange(job);
                  const subtitle = [job.employer, range]
                    .filter(Boolean)
                    .join(" \u00b7 ");
                  return (
                    <div key={index} className="mt-1">
                      {job.title && (
                        <div className="text-sm text-gray-700">{job.title}</div>
                      )}
                      {subtitle && (
                        <div className="text-xs text-gray-500">{subtitle}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {renderMatchingEntities ? (
            <div className="mt-3">
              <MatchingEntitiesConfirm item={profile} />
            </div>
          ) : (
            chips.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {chips.map((chip, index) => (
                  <InfoChip
                    key={`${chip.label}-${chip.value}-${index}`}
                    label={chip.label}
                    value={chip.value}
                  />
                ))}
              </div>
            )
          )}
        </>
      )}
      {modal}
      {showAddToReport && (
        <EntityUsedToConfirmModal
          open={entityModalOpen}
          onClose={() => setEntityModalOpen(false)}
          onSubmit={handleEntityConfirmSubmit}
          initialEntities={mapDiscoveryMatchesToConfirmEntities(
            content.discovery_matches
          )}
        />
      )}
    </div>
  );
};

export const getProfilerTrustCardProps = (profile: ItemWithMetaData) => {
  const { hasTrust } = getProfileTrust(profile);
  return {
    showTrustBadge: hasTrust,
    compactBadge: hasTrust,
    showMatchingEntities: !hasTrust,
  };
};

// Profiler "Review" container body: a flat list ranked by trust score, each card
// carrying its own compact pale badge.
export const ProfilerReviewList = ({
  suggestedProfiles,
  isReadOnly,
  handleAddRating,
  handleDelete,
  pendingItemUUIDs,
  pendingRemoveToastUUIDs,
  skipRemoveConfirmation,
}: {
  suggestedProfiles: ItemWithMetaData[];
  isReadOnly?: boolean;
  handleAddRating?: (
    itemUUID: string,
    confirmation?: ProfileAddConfirmation,
    existingContent?: ConfirmContent
  ) => void;
  handleDelete?: (itemUUID: string) => void;
  pendingItemUUIDs?: ReadonlySet<string>;
  pendingRemoveToastUUIDs?: ReadonlySet<string>;
  skipRemoveConfirmation?: boolean;
}) => {
  if (suggestedProfiles.length === 0) {
    return <div className="text-sm text-gray-500">No profiles to review</div>;
  }
  const trustProfiles = suggestedProfiles.filter(
    (p) => getProfileTrust(p).hasTrust
  );
  const legacyProfiles = suggestedProfiles.filter(
    (p) => !getProfileTrust(p).hasTrust
  );
  const rankedTrust = sortProfilesByTrustScore(trustProfiles);
  return (
    <div>
      {rankedTrust.map((profile) => (
        <ProfileCard
          key={profile.item.external_id}
          {...getProfilerTrustCardProps(profile)}
          profile={profile}
          isReadOnly={isReadOnly}
          handleAddRating={handleAddRating}
          handleDelete={handleDelete}
          pendingItemUUIDs={pendingItemUUIDs}
          pendingRemoveToastUUIDs={pendingRemoveToastUUIDs}
          skipRemoveConfirmation={skipRemoveConfirmation}
        />
      ))}
      {legacyProfiles.map((profile) => (
        <ProfileCard
          key={profile.item.external_id}
          {...getProfilerTrustCardProps(profile)}
          profile={profile}
          isReadOnly={isReadOnly}
          handleAddRating={handleAddRating}
          handleDelete={handleDelete}
          pendingItemUUIDs={pendingItemUUIDs}
          pendingRemoveToastUUIDs={pendingRemoveToastUUIDs}
          skipRemoveConfirmation={skipRemoveConfirmation}
        />
      ))}
    </div>
  );
};
